import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';

import { useBasisTheoryToken } from 'common/hooks/use-basis-theory-token';
import { ACHFields } from 'common/components/payment/ach-fields';
import infoDarkGreen from 'core/assets/svg/info-dark-green.svg';
import BaseDialog from 'core/components/base-dialog';
import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import { useToast } from 'core/components/toast';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import { track } from 'core/helpers/analytics';
import useStyles from './add-bank-account-modal.styles';

const AddBankAccountModal = ({ open, onClose }) => {
  const classes = useStyles();
  const toast = useToast();
  const { user } = useSession();
  const { setFieldValue, setFieldTouched, values } = useFormikContext();
  const { getBasisTheoryACHToken } = useBasisTheoryToken();

  // ACHFields writes these validity flags to Formik on each change event; gate the Add button on
  // them so we never tokenize an incomplete BasisTheory element.
  const achFieldsValid = values.isRoutingNumberValid && values.isAccountNumberValid && values.isAccountHolderNameValid;

  const {
    account: {
      id: accountId,
      policies: {
        policy: {
          policy: { id },
          billingDetails,
          addBankAccount,
          stripeCustomerId
        }
      }
    }
  } = useStore();

  const addAccount = async () => {
    try {
      const basisTheoryBankAccountTokenInfo = await getBasisTheoryACHToken();

      const last4 = basisTheoryBankAccountTokenInfo.data.account_number.slice(-4); // "XXXXX2023" -> "2023"
      const basisTheoryBankAccountToken = basisTheoryBankAccountTokenInfo.id;

      // check if existing bank account (legacy ba_* or pm_* us_bank_account)
      const bankAccountExists = billingDetails?.allPaymentMethods?.find(
        (existingPayment) =>
          last4.endsWith(existingPayment.last4) &&
          (existingPayment.id?.startsWith('ba') ||
            existingPayment.type === 'us_bank_account' ||
            existingPayment.bankName)
      );
      if (bankAccountExists) {
        throw new Error(`Bank account ending in ${last4} already exists`);
      }

      const accountData = {
        id: basisTheoryBankAccountTokenInfo.id,
        basisTheoryBankAccountToken,
        routingNumber: basisTheoryBankAccountTokenInfo.data.routing_number,
        last4
      };

      track('Staff - Changed Payment Method', {
        oldPaymentMethod: billingDetails?.activePaymentMethod?.bankName ? 'ACH' : 'CC',
        newPaymentMethod: 'ACH',
        last4,
        policyId: id,
        customerId: accountId,
        staffUser: user.username
      });
      setFieldValue('defaultBankAccount', accountData);
      addBankAccount(accountData);
      setFieldTouched('stripeCustomerId', values.stripeCustomerId !== stripeCustomerId);
      setFieldValue('stripeCustomerId', values.stripeCustomerId);
      onClose();
    } catch (error) {
      await toast.notify({
        type: 'error',
        message: error.message
      });
    }
  };

  return (
    <BaseDialog open={open} onClose={onClose} className={classes.container}>
      <div className={classes.content}>
        <Label type="h1" className={classes.title}>
          Add new bank account
        </Label>
        <div className="HIDDEN_FROM_SCREEN_SHARING">
          <ACHFields mode="light" />
        </div>
        <div className={classes.tip}>
          <img src={infoDarkGreen} alt="info" />
          <Label type="notificationText">
            <span className={classes.tipLabel}>User tip:</span> To set this new account as the default, update it on the
            settings tab. To remove the old account, go to the payment &amp; price tab.
          </Label>
        </div>
        <div className={classes.actions}>
          <Button variant="contained" color="secondary" onClick={addAccount} disabled={!achFieldsValid}>
            Add account
          </Button>
        </div>
      </div>
    </BaseDialog>
  );
};

AddBankAccountModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default observer(AddBankAccountModal);
